// SearchSuggestions.tsx - Dropdown showing quick action suggestions

import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { SearchAction, featuredActions, getActionById } from '../config/searchActions';

interface SearchSuggestionsProps {
  isVisible: boolean;
  onClose: () => void;
  onSelectExample: (example: string) => void;
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  isVisible,
  onClose,
  onSelectExample,
}) => {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  // Get featured actions (most commonly used)
  const featured = featuredActions
    .map(id => getActionById(id))
    .filter((action): action is SearchAction => action !== undefined);

  const handleExampleClick = (example: string) => {
    onSelectExample(example);
    onClose();
  };

  const handleShowAllClick = () => {
    navigate('/search-actions');
    onClose();
  };

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[500px] overflow-y-auto"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <SparklesIcon className="h-5 w-5 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Quick Actions</h3>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Try one of these commands to get started
        </p>
      </div>

      {/* Featured Actions */}
      <div className="py-2">
        {featured.map((action) => (
          <div
            key={action.id}
            className="px-4 py-3 hover:bg-accent/50 transition-colors"
          >
            <div className="flex items-start gap-3">
              <action.icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground mb-1">
                  {action.name}
                </div>
                <div className="text-xs text-muted-foreground mb-2">
                  {action.description}
                </div>
                {/* Show first 2 examples */}
                <div className="space-y-1">
                  {action.examples.slice(0, 2).map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleExampleClick(example)}
                      className="block w-full text-left px-2 py-1 text-xs rounded bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <span className="opacity-60">Try: </span>
                      <span className="font-mono">{example}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer with "Show All Actions" link */}
      <div className="px-4 py-3 border-t border-border bg-muted/30">
        <button
          onClick={handleShowAllClick}
          className="flex items-center justify-between w-full text-sm font-medium text-primary hover:text-primary/80 transition-colors group"
        >
          <span>View all available actions & examples</span>
          <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    </div>
  );
};

export default SearchSuggestions;
